import React, { useEffect, useState } from "react";
import {
  Fab,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PopUpModal from "./PopUpModal";

const HelpFloatingButton = () => {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);

  useEffect(() => {
    // Single delayed pulse on first load
    const t = setTimeout(() => setPulse(false), 2600);
    return () => clearTimeout(t);
  }, []);

  const content = (
    <Box sx={{ p: 2, position: "relative", '& .MuiTypography-root': { fontSize: '0.98rem' } }}>
      <IconButton
        onClick={() => setOpen(false)}
        aria-label="close-help"
        sx={{ position: "absolute", right: 8, top: 8, color: "#8B2757" }}
      >
        <IconX size={18} />
      </IconButton>

      <Typography variant="h5" sx={{ mb: 1, color: "#8B2757" }}>
        Help & User Guide
      </Typography>

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Welcome
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Welcome to "Suwa Arana" Donation Reservation System. This platform
        allows donors to support hospitals by reserving meaningful sponsorship
        dates through different donation packages. Your contribution helps
        hospitals continue providing essential healthcare services.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Donation Packages
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
        1. "Sponsor Lifetime" Package
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        This package allows donors to reserve a meaningful date for a period of
        100 years through a one-time contribution. Once confirmed, the selected
        date will remain dedicated under the donor’s reservation each year
        throughout the reservation period. Many donors choose this option to
        honor loved ones, commemorate special memories, celebrate important
        occasions.
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2 }}>
        2. "Sponsor a Day" Package
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        The Day Sponsor Package allows donors to support hospitals by reserving
        a meaningful date either once or on a recurring basis. A one-time
        reservation is ideal for special occasions or moments of remembrance.
        The recurring option lets donors continue their support monthly or
        annually within a selected time period, starting from a chosen date.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Reservation Rules
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        To ensure fairness and clarity, each date can be reserved by only one
        donor. Once a date is reserved, it will no longer be available for
        selection. Reservations are confirmed after payment or approval, and
        recurring reservations are automatically adjusted to avoid any conflicts
        or overlapping dates.
      </Typography>

      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Date Legend
      </Typography>
      <Box sx={{ display: "flex", gap: 3, alignItems: "center", mt: 1, mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 14, height: 14, bgcolor: "#d6a3bb", borderRadius: 1 }} />
          <Typography variant="body2">Reserved — unavailable for selection</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 14, height: 14, bgcolor: "#D3D3D3", borderRadius: 1 }} />
          <Typography variant="body2">Blocked — unavailable for selection</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Step-by-Step Reservation Process
      </Typography>
      <Box component="ol" sx={{ pl: 3, mt: 1, mb: 1 }}>
        <li>
          <Typography variant="body2">
            <strong>Step 1 — Select Package:</strong> Choose the donation
            package that best fits your intent (100-year Sponsor Lifetime or
            Sponsor a Day).
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Step 2 — Choose Date(s):</strong> Pick available
            sponsorship date(s). Unavailable or reserved dates are disabled.
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Step 3 — Enter Donor Information:</strong> Provide your
            full name, contact number, email and other requested details.
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Step 4 — Review & Confirm:</strong> Verify package,
            selected date(s) and donor information before confirming.
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Step 5 — Payment Options:</strong> Pay immediately via
            card or choose to reserve now and pay later according to the
            instructions provided.
          </Typography>
        </li>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Frequently Asked Questions (FAQ)
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Q: Can two donors reserve the same date?
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          A: No. Each date can be reserved by only one donor to ensure
          fairness.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Q: What happens if my selected date is already reserved?
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          A: The system automatically prevents selection of unavailable dates;
          reserved dates will appear disabled on the calendar.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Q: Can I reserve multiple dates?
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          A: Yes — depending on the package selected and date availability.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Q: Can I cancel or modify a reservation?
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          A: Please contact the administration team for assistance with
          changes or cancellations; they can guide you through the process.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        Important Information
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        All donations directly support hospital services and patient care.
        Reservation availability is updated in real time. Payments are processed
        securely and confirmation details will be shared via email or SMS if
        available.
      </Typography>

      <Typography variant="body2" sx={{ mt: 1 }}>
        Need Assistance? If you need help selecting a package, payment issues,
        reservation changes, or technical support, please contact our support
        team through the Contact Us page.
      </Typography>
    </Box>
  );

  return (
    <>
      <style>{`@keyframes help-pulse { 0% { transform: scale(1); } 50% { transform: scale(1.06); } 100% { transform: scale(1); } }`}</style>

      <Fab
        color="primary"
        aria-label="help"
        onClick={() => setOpen(!open)}
        sx={{
          position: "fixed",
          right: { xs: 12, md: 24 },
          bottom: { xs: 12, md: 24 },
          bgcolor: "#8B2757",
          color: "white",
          zIndex: 1400,
          boxShadow: 3,
          textTransform: "none",
          "&:hover": { bgcolor: "#7a2049" },
          animation: pulse ? "help-pulse 800ms ease-in-out" : "none",
        }}
      >
        <HelpOutlineIcon />
        {/* <Box component="span" sx={{ ml: 1, display: { xs: "none", sm: "inline-flex" } }}>
          Help
        </Box> */}
      </Fab>

      <PopUpModal
        open={open}
        handleCloseDialog={() => setOpen(false)}
        content={content}
      />
    </>
  );
};

export default HelpFloatingButton;
